import React, { useEffect, useState } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { Dropdown } from "react-bootstrap";

import { UseOutletContextCompany } from "../Company";
import { StateLanguage } from "../../../../languages/config/StateLanguage";
import { ApiDigitalAuthCompany } from "../../../../services/auth/company.service";
import { Error } from "../../../../models/error.model";
import { Message } from "../../../../models/message.model";
import { GlobalSwal } from "../../../../styles/swal.style";
import { legendValidInvalidRestart, legendIconValidInvalidRestart, legendInvalid } from "../../../../utils/legend.data.util";
import { uploadTooltip } from "../../../../utils/tooltip.util";
import { evaluateLegendOptionalIcon, evaluateLegendRequired } from "../../../../scripts/validate.legend.script";
import { expressions } from "../../../../libraries/regular.expressions.library";
import { taxMethodCompanyValue, taxMethodCompanyList } from "../../../../libraries/tax.method.company.library";
import { getError, getMessage } from "../../../../languages/translations/response";
import { errorWebHandler } from "../../../../scripts/error.web.handler.script";
import { taxDeclarationCompanyValue, taxDeclarationCompanyList } from "../../../../libraries/tax.declaration.company.library";
import { taxPeriodMethodCompanyList, taxPeriodCompanyValue, allowedPeriodicitiesForMethod, defaultPeriodicityForMethod } from "../../../../libraries/tax.statement.company.library";
import { taxStatementCompanyList } from "../../../../libraries/tax.period.company.library";
import { SettingsDA } from "../../../../services/admin/settings.service";
import { AccountingAccountDA } from "../../../../services/admin/accounting.account.service";

import AppInputQrType from "../../../../components/element/InputQrType";
import AppLoading from "../../../../components/Loading";
import AppLegend from "../../../../components/element/Legend";

export interface AppCompanyUpdateAccountingProps {}

// Add to expressions library: taxrate: /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/
// Validates: 0-100 with up to 2 decimal places (e.g. 8, 7.7, 2.50, 100)

let errorResponse: Error, messageResponse: Message;

const AppCompanyUpdateAccounting: React.FunctionComponent<AppCompanyUpdateAccountingProps> = (props) => {
  const { appServiceForUser, companyForUser, settingsForCompany, reloadSession } = UseOutletContextCompany();
  const { lang } = StateLanguage();

  const [loadIndicator, setLoadIndicator] = useState("off");
  const [registerNumber, setRegisterNumber] = useState({ value: "", valid: false });
  const [qrType, setQrType] = useState({ value: "", valid: false });
  const [subjectTax, setSubjectTax] = useState({ value: false, valid: false });
  const [taxMethod, setTaxMethod] = useState({ value: "", valid: false });
  const [taxDeclaration, setTaxDeclaration] = useState({ value: "", valid: false });
  const [taxRate, setTaxRate] = useState({ value: "", valid: false });
  const [fiscalPeriods, setFiscalPeriods] = useState<any[]>([]);
  const [hasPeriods, setHasPeriods] = useState(false);
  const [hasEntries, setHasEntries] = useState(false);
  const [declarationPeriodicity, setDeclarationPeriodicity] = useState({ value: "", valid: false });
  const [taxPeriodicity, setTaxPeriodicity] = useState({ value: "", valid: false });
  const [firstPeriod, setFirstPeriod] = useState({ value: "", valid: false });

  const isAccountingLocked = hasEntries || hasPeriods;

  const deducePeriodicity = (periodName: string): string => {
    if (periodName.includes('-Q')) return 'quarterly';
    if (periodName.includes('-S')) return 'semiannual';
    if (periodName.includes('-M')) return 'monthly';
    return '';
  };

  // Determines if taxRate input is required based on tax method
  const taxRateRequired = taxMethod.value === "tdfn" || taxMethod.value === "taf";

  // Validates all required fields considering conditional visibility
  const formIsValid = (): boolean => {
    if (!qrType.valid) return false;
    if (!subjectTax.valid) return false;
    if (subjectTax.value && !isAccountingLocked) {
      if (!taxMethod.valid) return false;
      if (taxRateRequired && !taxRate.valid) return false;
      if (!taxDeclaration.valid) return false;
      if (!hasPeriods) {
        if (!taxPeriodicity.valid) return false;
        if (!firstPeriod.valid) return false;
      }
    }
    return true;
  };

  const submitCompanyUpdate = async () => {
    setLoadIndicator("on");

    if (appServiceForUser && companyForUser && settingsForCompany && formIsValid()) {
      const vatResponse = await AccountingAccountDA.putVatRegime(
        companyForUser.id,
        subjectTax.value,
        subjectTax.value ? taxMethod.value : "",
        subjectTax.value && taxRateRequired ? taxRate.value : "",
        subjectTax.value ? taxDeclaration.value : "",
        subjectTax.value ? declarationPeriodicity.value : "",
      )

      if (vatResponse.status !== 200) {
        errorResponse = vatResponse.data;
        GlobalSwal.fire({
          title: getError(errorResponse?.code ?? 'UNKNOWN', lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: "error",
          confirmButtonText: lang.labels.okGotIt,
        });
        setLoadIndicator("off");
        return;
      }

      const settingsResponse = await SettingsDA.putSettings(
        qrType.value,
        settingsForCompany.language,
        settingsForCompany.colors,
      )

      if (settingsResponse.status !== 200) {
        errorResponse = settingsResponse.data;
        GlobalSwal.fire({
          title: getError(errorResponse?.code ?? 'UNKNOWN', lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: "error",
          confirmButtonText: lang.labels.okGotIt,
        });
        setLoadIndicator("off");
        return;
      }

      if (subjectTax.value && !hasPeriods && taxPeriodicity.valid && firstPeriod.valid) {
        const fiscalResponse = await AccountingAccountDA.createFiscalPeriod(companyForUser.id, {
          tax_period: taxPeriodicity.value,
          tax_statement: firstPeriod.value,
          regime: taxMethod.value,
          method: settingsForCompany.tax_declaration || taxDeclaration.value,
        });

        if (fiscalResponse.status !== 200 && fiscalResponse.status !== 201) {
          errorResponse = fiscalResponse.data;
          GlobalSwal.fire({
            title: getError(errorResponse?.code ?? 'UNKNOWN', lang.code, errorResponse?.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: "error",
            confirmButtonText: lang.labels.okGotIt,
          });
          setLoadIndicator("off");
          return;
        }
      }

      reloadSession(null, null, null, () => {
        GlobalSwal.fire({
          title: lang.labels.successfullyUpdatedCompanySettings,
          text: lang.labels.actionCompletedReturningToPage,
          icon: "success",
          showConfirmButton: false,
          timer: 2200,
        }).then(() => {
          restartForm();
          if (companyForUser) loadFiscalPeriods(companyForUser.id);
        });
      });
      setLoadIndicator("off");
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {
        if (!qrType.value) legendInvalid("legend-qr-type-required");
        if (!subjectTax.valid) legendInvalid("legend-subject-tax-required");
        if (subjectTax.value) {
          if (!taxMethod.valid) legendInvalid("legend-tax-method-required");
          if (taxRateRequired && !taxRate.valid) legendInvalid("legend-tax-rate-valid");
          if (!taxDeclaration.valid) legendInvalid("legend-tax-declaration-required");
          if (!hasPeriods) {
            if (!taxPeriodicity.valid) legendInvalid("legend-tax-periodicity-required");
            if (!firstPeriod.valid) legendInvalid("legend-first-period-required");
          }
        }
        setLoadIndicator("off");
      });
    }
  };

  const handleChangeSubjectTax = (value: boolean) => {
    setSubjectTax({ value, valid: true });
    if (!value) {
      setTaxMethod({ value: "", valid: false });
      setTaxDeclaration({ value: "", valid: false });
      setTaxRate({ value: "", valid: false });
    }
  };

  useEffect(() => {
    if (subjectTax.valid && subjectTax.value) {
      uploadTooltip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectTax.value]);

  const handleChangeTaxMethod = (code: string) => {
    setTaxMethod({ value: code, valid: true });
    // Reset taxRate when switching away from methods that require it
    if (code !== "tdfn" && code !== "taf") {
      setTaxRate({ value: "", valid: false });
    }
    // Reset declaration periodicity if current value is invalid for the new method
    const allowed = allowedPeriodicitiesForMethod(code);
    if (declarationPeriodicity.value && !allowed.includes(declarationPeriodicity.value)) {
      const newDefault = defaultPeriodicityForMethod(code);
      setDeclarationPeriodicity({ value: newDefault, valid: newDefault.length > 0 });
    }
    // Reset fiscal period fields when tax method changes
    if (!hasPeriods) {
      setTaxPeriodicity({ value: "", valid: false });
      setFirstPeriod({ value: "", valid: false });
    }
  };

  const handleChangeTaxDeclaration = (code: string) => {
    setTaxDeclaration({ value: code, valid: true });
  };

  const handleChangeTaxRate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTaxRate({ ...taxRate, value: event.target.value });
  };

  const validateTaxRate = () => {
    // taxrate regex: /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/
    // Validates percentages from 0 to 100 with up to 2 decimal places
    evaluateLegendOptionalIcon(expressions.taxrate, taxRate, setTaxRate, "input-tax-rate", "legend-tax-rate-valid");
  };

  const handleChangeTaxPeriodicity = (code: string) => {
    setTaxPeriodicity({ value: code, valid: true });
    setDeclarationPeriodicity({ value: code, valid: true });
    setFirstPeriod({ value: "", valid: false });
  };

  const handleChangeFirstPeriod = (code: string) => {
    setFirstPeriod({ value: code, valid: true });
  };

  const loadFiscalPeriods = async (id_company: string) => {
    const response = await AccountingAccountDA.getFiscalPeriods(id_company);
    if (response.status === 200) {
      const periods = Array.isArray(response.data) ? response.data : response.data?.periods ?? [];
      setFiscalPeriods(periods);
      setHasPeriods(periods.length > 0);
    }
  };

  const executeFormRestart = () => {
    uploadData();
    restartForm();
  };

  function restartForm() {
    legendIconValidInvalidRestart("input-register-number", "legend-register-number-valid");
    legendIconValidInvalidRestart("input-tax-rate", "legend-tax-rate-valid");
    legendValidInvalidRestart("legend-subject-tax-required");
    legendValidInvalidRestart("legend-tax-method-required");
    legendValidInvalidRestart("legend-tax-declaration-required");
    legendValidInvalidRestart("legend-qr-type-required");
    legendValidInvalidRestart("legend-tax-periodicity-required");
    legendValidInvalidRestart("legend-first-period-required");
  }

  function uploadData() {
    if (companyForUser && settingsForCompany) {
      setRegisterNumber({ value: companyForUser.register_number, valid: true });
      setQrType({ value: settingsForCompany.qr_type, valid: true });
      setSubjectTax({ value: settingsForCompany.subject_tax, valid: true });

      if (settingsForCompany.subject_tax) {
        setTaxMethod({ value: settingsForCompany.tax_method, valid: settingsForCompany.tax_method.length > 0 });
        setTaxDeclaration({ value: settingsForCompany.tax_declaration, valid: settingsForCompany.tax_declaration.length > 0 });
        setTaxRate({ value: settingsForCompany.tax_rate ?? "", valid: (settingsForCompany.tax_rate ?? "").length > 0 });
      } else {
        setTaxMethod({ value: "", valid: false });
        setTaxDeclaration({ value: "", valid: false });
        setTaxRate({ value: "", valid: false });
      }
    }
  }

  useEffect(() => {
    // Reset all async-loaded state immediately to avoid showing stale data from previous company
    setFiscalPeriods([]);
    setHasPeriods(false);
    setHasEntries(false);
    setDeclarationPeriodicity({ value: "", valid: false });
    setTaxPeriodicity({ value: "", valid: false });
    setFirstPeriod({ value: "", valid: false });

    uploadData();
    uploadTooltip();
    if (companyForUser) {
      loadFiscalPeriods(companyForUser.id);

      AccountingAccountDA.getVatRegime(companyForUser.id).then(response => {
        if (response.status === 200 && response.data) {
          const dp = response.data.declaration_periodicity || '';
          setDeclarationPeriodicity({ value: dp, valid: dp.length > 0 });
        }
      });

      AccountingAccountDA.getEntries(companyForUser.id, { limit: '1' }).then(response => {
        if (response.status === 200) {
          const data = response.data;
          const entries = Array.isArray(data) ? data : (data?.items || []);
          setHasEntries(entries.length > 0);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, settingsForCompany]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title fs-4 fw-bolder">{lang.labels.companyAccountingSettings}</div>
      </div>
      {companyForUser && settingsForCompany ? (
        <div className="form">
          <div className="card-body">
            {/* Register number - hidden */}
            <div className="row mb-3 d-none">
              <div className="col-xl-3">
                <div className="col-form-label">{lang.labels.registerNumber}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-register-number"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="register-number"
                  value={registerNumber.value}
                  disabled
                />
              </div>
            </div>

            {/* QR Type */}
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.qrType}</span>
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.shapesOrVersionsOfQR}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
              </div>
            </div>

            {/* Locked info banners */}
            {isAccountingLocked && (
              <div className="notice d-flex align-items-center bg-light-warning border border-warning border-dashed rounded w-100 p-5 mb-5">
                <FaExclamationCircle className="fs-2x text-warning me-3" />
                <div className="fw-bold text-dark">
                  <strong>{lang.labels.important}</strong>
                  <br />
                  {lang.labels.taxSettingsLockedContact}
                  {' '}
                  <a href="mailto:support@digitaladmin.ch">support@digitaladmin.ch</a>
                  {' '}{lang.labels.or}{' '}
                  <a href="tel:+41792830646">+41 79 283 06 46</a>.
                </div>
              </div>
            )}

            {/* Subject to VAT */}
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.subjectToVat}
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.ifSoAdditionalInformationRequired}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <div className="d-flex row mx-0 mt-2">
                  <label className="col-auto form-check form-check-lg form-check-custom form-check-solid me-5">
                    <input
                      className="form-check-input"
                      type="radio"
                      value="yes"
                      checked={subjectTax.value === true}
                      onChange={() => handleChangeSubjectTax(true)}
                      disabled={isAccountingLocked}
                    />
                    <span className="form-check-label fw-bold text-uppercase fs-7">{lang.labels.yes}</span>
                  </label>
                  <label className="col-auto form-check form-check-lg form-check-custom form-check-solid">
                    <input
                      className="form-check-input"
                      type="radio"
                      value="no"
                      checked={subjectTax.value === false && subjectTax.valid}
                      onChange={() => handleChangeSubjectTax(false)}
                      disabled={isAccountingLocked}
                    />
                    <span className="form-check-label fw-bold text-uppercase fs-7">{lang.labels.no}</span>
                  </label>
                </div>
                <AppLegend
                  attribute={{ check: subjectTax.valid, name: "subject-tax", index: null, sub_index: null }}
                  container={{ valid: false, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>

            {/* Conditional fields when subject to VAT */}
            {subjectTax.valid && subjectTax.value && (
              <>
                {/* VAT Accounting Type (tax_method) */}
                <div className="row mb-3">
                  <div className="col-xl-3">
                    <div className="col-form-label">
                      <div className="d-flex align-items-center">
                        <span className="required">{lang.labels.vatAccountingType}</span>
                        <i
                          className="ms-2 fs-8"
                          data-bs-toggle="tooltip"
                          data-bs-trigger="hover"
                          data-bs-original-title={lang.labels.theVatRegimeCanOnlyChangedOnceAYear}
                        >
                          <FaExclamationCircle />
                        </i>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-9 fv-row fv-plugins-icon-container">
                    {isAccountingLocked ? (
                      <input className="form-control" type="text" value={taxMethodCompanyValue(lang, taxMethod.value)} disabled />
                    ) : (
                      <Dropdown>
                        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                          <span className="selection">
                            <span className="select2-selection select2-selection--single form-select">
                              <span className="select2-selection__rendered" role="textbox">
                                {taxMethod.valid ? taxMethodCompanyValue(lang, taxMethod.value) : lang.labels.selectOption}
                              </span>
                            </span>
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                          <span className="select2-dropdown select2-dropdown--below">
                            <span className="select2-results">
                              <ul className="select2-results__options" role="listbox">
                                {taxMethodCompanyList(lang).map((item, index) => (
                                  <li
                                    key={index}
                                    className={`select2-results__option select2-results__option--selectable ${item.code === taxMethod.value && "select2-results__option--selected"}`}
                                    role="option"
                                    aria-selected={item.code === taxMethod.value}
                                  >
                                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxMethod(item.code)}>
                                      {item.display}
                                    </Dropdown.Item>
                                  </li>
                                ))}
                              </ul>
                            </span>
                          </span>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    <AppLegend
                      attribute={{ check: taxMethod.valid, name: "tax-method", index: null, sub_index: null }}
                      container={{ valid: false, required: true, identical: false, exists: false }}
                    ></AppLegend>
                  </div>
                </div>

                {/* Tax Rate - only when method is tdfn or taf */}
                {taxRateRequired && (
                  <div className="row mb-3">
                    <div className="col-xl-3">
                      <div className="col-form-label">
                        <div className="d-flex align-items-center">
                          <span className="required">{lang.labels.taxRate}</span>
                          <i
                            className="ms-2 fs-8"
                            data-bs-toggle="tooltip"
                            data-bs-trigger="hover"
                            data-bs-original-title={lang.labels.enterApplicableVatRate}
                          >
                            <FaExclamationCircle />
                          </i>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-9 fv-row fv-plugins-icon-container">
                      <input
                        id="input-tax-rate"
                        className="form-control"
                        type="text"
                        autoComplete="off"
                        name="tax-rate"
                        value={taxRate.value}
                        onChange={handleChangeTaxRate}
                        onKeyUp={validateTaxRate}
                        onBlur={validateTaxRate}
                        disabled={isAccountingLocked}
                      />
                      <AppLegend
                        attribute={{ check: taxRate.valid, name: "tax-rate", index: null, sub_index: null }}
                        container={{ valid: true, required: true, identical: false, exists: false }}
                      ></AppLegend>
                    </div>
                  </div>
                )}

                {/* Declaration Method (tax_declaration) */}
                <div className="row mb-3">
                  <div className="col-xl-3">
                    <div className="col-form-label">
                      <div className="d-flex align-items-center">
                        <span className="required">{lang.labels.declarationMethod}</span>
                        <i
                          className="ms-2 fs-8"
                          data-bs-toggle="tooltip"
                          data-bs-trigger="hover"
                          data-bs-original-title={lang.labels.dependsWhetherCompanySubjectTax}
                        >
                          <FaExclamationCircle />
                        </i>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-9 fv-row fv-plugins-icon-container">
                    {isAccountingLocked ? (
                      <input className="form-control" type="text" value={taxDeclarationCompanyValue(lang, taxDeclaration.value)} disabled />
                    ) : (
                      <Dropdown>
                        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                          <span className="selection">
                            <span className="select2-selection select2-selection--single form-select">
                              <span className="select2-selection__rendered" role="textbox">
                                {taxDeclaration.valid ? taxDeclarationCompanyValue(lang, taxDeclaration.value) : lang.labels.selectOption}
                              </span>
                            </span>
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                          <span className="select2-dropdown select2-dropdown--below">
                            <span className="select2-results">
                              <ul className="select2-results__options" role="listbox">
                                {taxDeclarationCompanyList(lang).map((item, index) => (
                                  <li
                                    key={index}
                                    className={`select2-results__option select2-results__option--selectable ${item.code === taxDeclaration.value && "select2-results__option--selected"}`}
                                    role="option"
                                    aria-selected={item.code === taxDeclaration.value}
                                  >
                                    <Dropdown.Item
                                      bsPrefix="select2-results__option__text"
                                      onClick={() => handleChangeTaxDeclaration(item.code)}
                                    >
                                      {item.display}
                                    </Dropdown.Item>
                                  </li>
                                ))}
                              </ul>
                            </span>
                          </span>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                    <AppLegend
                      attribute={{ check: taxDeclaration.valid, name: "tax-declaration", index: null, sub_index: null }}
                      container={{ valid: false, required: true, identical: false, exists: false }}
                    ></AppLegend>
                  </div>
                </div>

                {/* Fiscal Period Configuration */}
                {hasPeriods ? (
                  <>
                    <div className="row mb-3">
                      <div className="col-xl-3">
                        <div className="col-form-label">{lang.labels.vatPeriodicity}</div>
                      </div>
                      <div className="col-xl-9 fv-row">
                        <input className="form-control" type="text" value={taxPeriodCompanyValue(lang, declarationPeriodicity.value || deducePeriodicity(fiscalPeriods[0]?.name ?? ''))} disabled />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* VAT Periodicity */}
                    <div className="row mb-3">
                      <div className="col-xl-3">
                        <div className="col-form-label">
                          <span className="required">{lang.labels.vatPeriodicity}</span>
                        </div>
                      </div>
                      <div className="col-xl-9 fv-row fv-plugins-icon-container">
                        <Dropdown>
                          <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                            <span className="selection">
                              <span className="select2-selection select2-selection--single form-select">
                                <span className="select2-selection__rendered" role="textbox">
                                  {taxPeriodicity.valid ? taxPeriodCompanyValue(lang, taxPeriodicity.value) : lang.labels.selectOption}
                                </span>
                              </span>
                            </span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                            <span className="select2-dropdown select2-dropdown--below">
                              <span className="select2-results">
                                <ul className="select2-results__options" role="listbox">
                                  {taxPeriodMethodCompanyList(lang, taxMethod.value).map((item, index) => (
                                    <li
                                      key={index}
                                      className={`select2-results__option select2-results__option--selectable ${item.code === taxPeriodicity.value && "select2-results__option--selected"}`}
                                      role="option"
                                      aria-selected={item.code === taxPeriodicity.value}
                                    >
                                      <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxPeriodicity(item.code)}>
                                        {item.display}
                                      </Dropdown.Item>
                                    </li>
                                  ))}
                                </ul>
                              </span>
                            </span>
                          </Dropdown.Menu>
                        </Dropdown>
                        <AppLegend
                          attribute={{ check: taxPeriodicity.valid, name: "tax-periodicity", index: null, sub_index: null }}
                          container={{ valid: false, required: true, identical: false, exists: false }}
                        ></AppLegend>
                      </div>
                    </div>

                    {/* First Period */}
                    {taxPeriodicity.valid && companyForUser && (
                      <div className="row mb-3">
                        <div className="col-xl-3">
                          <div className="col-form-label">
                            <span className="required">{lang.labels.firstPeriod}</span>
                          </div>
                        </div>
                        <div className="col-xl-9 fv-row fv-plugins-icon-container">
                          <Dropdown>
                            <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                              <span className="selection">
                                <span className="select2-selection select2-selection--single form-select">
                                  <span className="select2-selection__rendered" role="textbox">
                                    {firstPeriod.valid
                                      ? taxStatementCompanyList(lang, companyForUser.created_at ?? '', taxPeriodicity.value).find(i => i.code === firstPeriod.value)?.display ?? firstPeriod.value
                                      : lang.labels.selectOption}
                                  </span>
                                </span>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                              <span className="select2-dropdown select2-dropdown--below">
                                <span className="select2-results">
                                  <ul className="select2-results__options" role="listbox">
                                    {taxStatementCompanyList(lang, companyForUser.created_at ?? '', taxPeriodicity.value).map((item, index) => (
                                      <li
                                        key={index}
                                        className={`select2-results__option select2-results__option--selectable ${item.code === firstPeriod.value && "select2-results__option--selected"}`}
                                        role="option"
                                        aria-selected={item.code === firstPeriod.value}
                                      >
                                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeFirstPeriod(item.code)}>
                                          {item.display}
                                        </Dropdown.Item>
                                      </li>
                                    ))}
                                  </ul>
                                </span>
                              </span>
                            </Dropdown.Menu>
                          </Dropdown>
                          <AppLegend
                            attribute={{ check: firstPeriod.valid, name: "first-period", index: null, sub_index: null }}
                            container={{ valid: false, required: true, identical: false, exists: false }}
                          ></AppLegend>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Warning notice - commented out, may be re-enabled later */}
            {/* <div className="notice d-flex align-items-center bg-light-warning border border-warning border-dashed rounded w-100 p-5">
              <span className="svg-icon svg-icon-3x svg-icon-warning me-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="10" fill="black" />
                  <rect x="11" y="14" width="7" height="2" rx="1" transform="rotate(-90 11 14)" fill="black" />
                  <rect x="11" y="17" width="2" height="2" rx="1" transform="rotate(-90 11 17)" fill="black" />
                </svg>
              </span>
              <div className="d-grid">
                <span className="text-start text-dark fw-bold">{lang.labels.important}</span>
                <span className="text-justify text-secondary fw-bold">
                  {lang.labels.ifYouWishChangeVatRegimePleaseContact}
                  <a href="mailto:support@digitaladmin.ch" className="text-primary text-lowercase fw-bolder mx-1">support@digitaladmin.ch</a>
                  {lang.labels.orCall}
                  <a href="tel:+41792830646" className="text-primary text-lowercase fw-bolder mx-1">+41 79 283 06 46</a>
                  .
                </span>
              </div>
            </div> */}
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-secondary btn-active-light-primary" type="reset" onClick={executeFormRestart}>
              {lang.labels.discard}
            </button>
            <button className="btn btn-primary ms-2" type="button" data-kt-indicator={loadIndicator} onClick={submitCompanyUpdate}>
              <span className="indicator-label">{lang.labels.saveChanges}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      ) : (
        <AppLoading></AppLoading>
      )}
    </div>
  );
};

export default AppCompanyUpdateAccounting;
